<template>
    <SideDrawer :open="open" :title="title" :subtitle="subtitle" :busy="isSaving" @close="emit('close')">
        <form id="structure-drawer-form" class="structure-form" novalidate @submit.prevent="submit">
            <div v-if="kind === ENTITY_KIND.BRAND" class="field">
                <label class="field__label" for="structure-drawer-advertiser">Рекламодатель</label>
                <select
                    id="structure-drawer-advertiser"
                    v-model="form.advertiser_id"
                    :class="['select-input', { 'is-invalid': errors.advertiser_id }]"
                    :disabled="isSaving"
                >
                    <option value="" disabled>Выберите рекламодателя</option>
                    <optgroup v-for="group in advertiserGroups" :key="group.key" :label="group.label">
                        <option v-for="advertiser in group.advertisers" :key="advertiser.id" :value="String(advertiser.id)">
                            {{ advertiser.name }}
                        </option>
                    </optgroup>
                </select>
                <p v-if="errors.advertiser_id" class="field__error">{{ errors.advertiser_id }}</p>
                <p v-else-if="isAgencyBrandMoveRestricted" class="field__hint">
                    Бренд можно перенести только к рекламодателю того же партнёра.
                </p>
            </div>

            <div class="field">
                <label class="field__label" for="structure-drawer-name">Название</label>
                <input
                    id="structure-drawer-name"
                    v-model="form.name"
                    type="text"
                    maxlength="255"
                    autocomplete="off"
                    :class="['form-input', { 'is-invalid': errors.name }]"
                    :disabled="isSaving"
                />
                <p v-if="errors.name" class="field__error">{{ errors.name }}</p>
            </div>

            <div v-if="hasPlatformMarkupField" class="field">
                <label class="field__label" for="structure-drawer-markup">Платформенная наценка (L1), %</label>
                <input
                    id="structure-drawer-markup"
                    v-model="form.markup_percent"
                    type="number"
                    min="0"
                    :max="MAX_MARKUP_PERCENT"
                    step="0.01"
                    inputmode="decimal"
                    :class="['form-input', 'structure-form__number', { 'is-invalid': errors.markup_percent }]"
                    :disabled="isSaving"
                />
                <p v-if="errors.markup_percent" class="field__error">{{ errors.markup_percent }}</p>
                <p v-else class="field__hint">
                    База SSP → цена агентства, агентство её не видит. Позже наценки меняются через «Действия → Наценки».
                </p>
            </div>

            <div v-if="kind === ENTITY_KIND.ADVERTISER" class="field">
                <label class="field__label" for="structure-drawer-partner">Партнёр</label>
                <select
                    id="structure-drawer-partner"
                    v-model="form.partner_id"
                    :class="['select-input', { 'is-invalid': errors.partner_id }]"
                    :disabled="isSaving || !canChangePartner"
                >
                    <option v-if="actor.is_admin" value="">Без партнёра</option>
                    <option v-else value="" disabled>Выберите партнёра</option>
                    <option v-for="partner in partners" :key="partner.id" :value="String(partner.id)">{{ partner.name }}</option>
                </select>
                <p v-if="errors.partner_id" class="field__error">{{ errors.partner_id }}</p>
                <p v-else-if="!canChangePartner" class="field__hint">
                    Перенести рекламодателя к другому партнёру может только администратор.
                </p>
            </div>

            <div v-if="kind === ENTITY_KIND.BRAND" class="field">
                <label class="field__label" for="structure-drawer-description">
                    Описание <span class="field__optional">необязательно</span>
                </label>
                <textarea
                    id="structure-drawer-description"
                    v-model="form.description"
                    rows="3"
                    maxlength="2000"
                    :class="['form-input', { 'is-invalid': errors.description }]"
                    :disabled="isSaving"
                ></textarea>
                <p v-if="errors.description" class="field__error">{{ errors.description }}</p>
            </div>

            <div v-if="consequences.length" class="consequences" role="note">
                <p class="consequences__title">После сохранения</p>
                <ul>
                    <li v-for="item in consequences" :key="item">{{ item }}</li>
                </ul>
            </div>

            <p v-if="generalError" class="form-error" role="alert">{{ generalError }}</p>
        </form>

        <template #footer>
            <button type="button" class="btn-secondary" :disabled="isSaving" @click="emit('close')">Отмена</button>
            <button type="submit" form="structure-drawer-form" class="btn-primary" :disabled="!canSubmit || isSaving">
                {{ isSaving ? 'Сохранение…' : (isCreate ? 'Создать' : 'Сохранить') }}
            </button>
        </template>
    </SideDrawer>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import axios from 'axios';
import SideDrawer from '../../ui/SideDrawer.vue';
import {
    DEFAULT_PARTNER_MARKUP_PERCENT,
    ENTITY_ENDPOINT,
    ENTITY_KIND,
    ENTITY_LABEL,
    MAX_MARKUP_PERCENT,
} from '../../../constants/structure';
import { DRAWER_MODE } from '../../../constants/users';
import { extractErrorMessage, extractFieldErrors } from '../../../utils/httpErrors';
import { PLURAL, countLabel } from '../../../utils/plural';

const props = defineProps({
    open: { type: Boolean, required: true },
    mode: { type: String, required: true, validator: value => [DRAWER_MODE.CREATE, DRAWER_MODE.EDIT].includes(value) },
    kind: { type: String, required: true, validator: value => Object.values(ENTITY_KIND).includes(value) },
    /** Редактируемый узел дерева (entity + stats) — только в режиме edit. */
    node: { type: Object, default: null },
    /** Предвыбор родителя при создании из меню узла: { partnerId?, advertiserId? }. */
    parent: { type: Object, default: () => ({}) },
    actor: { type: Object, required: true },
    partners: { type: Array, required: true },
    advertisers: { type: Array, required: true },
});

const emit = defineEmits(['close', 'saved']);

const emptyForm = () => ({
    name: '',
    markup_percent: String(DEFAULT_PARTNER_MARKUP_PERCENT),
    partner_id: '',
    advertiser_id: '',
    description: '',
});

const form = reactive(emptyForm());
const errors = ref({});
const generalError = ref('');
const isSaving = ref(false);

const isCreate = computed(() => props.mode === DRAWER_MODE.CREATE);
const hasPlatformMarkupField = computed(() => props.kind === ENTITY_KIND.PARTNER && isCreate.value);
const entity = computed(() => props.node?.entity || null);
const toFormId = value => (value == null ? '' : String(value));

const resetForm = () => {
    Object.assign(form, emptyForm());
    errors.value = {};
    generalError.value = '';

    if (isCreate.value) {
        form.partner_id = toFormId(props.parent.partnerId ?? (props.actor.is_admin || props.partners.length !== 1 ? null : props.partners[0].id));
        form.advertiser_id = toFormId(props.parent.advertiserId);
        return;
    }

    form.name = entity.value.name;
    form.partner_id = toFormId(entity.value.partner_id);
    form.advertiser_id = toFormId(entity.value.advertiser_id);
    form.description = entity.value.description || '';
};

watch(() => props.open, (isOpen) => {
    if (isOpen) resetForm();
});

const label = computed(() => ENTITY_LABEL[props.kind]);
const title = computed(() => (isCreate.value
    ? `Новый ${label.value.one.toLowerCase()}`
    : `Редактировать ${label.value.accusative}`));
const subtitle = computed(() => (isCreate.value ? '' : entity.value?.name || ''));

const partnerNames = computed(() => new Map(props.partners.map(partner => [Number(partner.id), partner.name])));
const advertiserById = computed(() => new Map(props.advertisers.map(advertiser => [Number(advertiser.id), advertiser])));

const canChangePartner = computed(() => Boolean(props.actor.is_admin) || isCreate.value);

/** Agency переносит бренд только в пределах партнёра текущего рекламодателя. */
const isAgencyBrandMoveRestricted = computed(() => !props.actor.is_admin && !isCreate.value);

const advertiserGroups = computed(() => {
    let advertisers = props.advertisers;
    if (isAgencyBrandMoveRestricted.value) {
        const partnerId = advertiserById.value.get(Number(entity.value?.advertiser_id))?.partner_id;
        advertisers = advertisers.filter(advertiser => advertiser.partner_id === partnerId);
    }

    const groups = new Map();
    advertisers.forEach((advertiser) => {
        const key = advertiser.partner_id ?? 'none';
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                label: partnerNames.value.get(Number(advertiser.partner_id)) || 'Без партнёра',
                advertisers: [],
            });
        }
        groups.get(key).advertisers.push(advertiser);
    });
    return [...groups.values()];
});

const isValidMarkup = computed(() => form.markup_percent !== ''
    && Number(form.markup_percent) >= 0
    && Number(form.markup_percent) <= MAX_MARKUP_PERCENT);

const canSubmit = computed(() => {
    if (!form.name.trim()) return false;
    if (props.kind === ENTITY_KIND.PARTNER) return !hasPlatformMarkupField.value || isValidMarkup.value;
    if (props.kind === ENTITY_KIND.ADVERTISER) return Boolean(props.actor.is_admin || !isCreate.value || form.partner_id);
    return Boolean(form.advertiser_id);
});

const consequences = computed(() => {
    if (isCreate.value || !entity.value) return [];

    if (props.kind === ENTITY_KIND.ADVERTISER && toFormId(entity.value.partner_id) !== form.partner_id) {
        const target = form.partner_id ? `партнёру «${partnerNames.value.get(Number(form.partner_id))}»` : 'разделу «Без партнёра»';
        return [
            `Рекламодатель со всеми брендами и кампаниями перейдёт к ${target}.`,
            'Агентства прежнего партнёра потеряют к нему доступ.',
        ];
    }

    if (props.kind === ENTITY_KIND.BRAND && toFormId(entity.value.advertiser_id) !== form.advertiser_id) {
        const campaignsCount = props.node.stats.campaigns.total;
        const target = advertiserById.value.get(Number(form.advertiser_id))?.name;
        return [
            `Бренд перейдёт к рекламодателю «${target}».`,
            campaignsCount
                ? `Вместе с ним перейдут ${countLabel(campaignsCount, PLURAL.CAMPAIGN)}.`
                : 'Кампаний у бренда нет.',
        ];
    }

    return [];
});

const buildPayload = () => {
    const name = form.name.trim();

    if (props.kind === ENTITY_KIND.PARTNER) {
        return hasPlatformMarkupField.value ? { name, markup_percent: Number(form.markup_percent) } : { name };
    }

    if (props.kind === ENTITY_KIND.ADVERTISER) {
        return canChangePartner.value
            ? { name, partner_id: form.partner_id ? Number(form.partner_id) : null }
            : { name };
    }

    return { name, advertiser_id: Number(form.advertiser_id), description: form.description.trim() || null };
};

const request = payload => (isCreate.value
    ? axios.post(ENTITY_ENDPOINT[props.kind], payload)
    : axios.put(`${ENTITY_ENDPOINT[props.kind]}/${entity.value.id}`, payload));

const submit = async () => {
    if (!canSubmit.value || isSaving.value) return;

    isSaving.value = true;
    errors.value = {};
    generalError.value = '';

    try {
        const { data } = await request(buildPayload());
        const saved = data[props.kind];
        emit('saved', {
            kind: props.kind,
            mode: props.mode,
            entity: saved,
            message: isCreate.value ? `${label.value.one} «${saved.name}» создан` : 'Изменения сохранены',
        });
    } catch (error) {
        errors.value = extractFieldErrors(error);
        if (!Object.keys(errors.value).length) generalError.value = extractErrorMessage(error);
    } finally {
        isSaving.value = false;
    }
};
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;
@use '../../../../scss/pages/admin/admin-form';

@include admin-form.fields;

.structure-form {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
}

.structure-form__number {
    max-width: 160px;
}

.consequences {
    padding: $spacing-md $spacing-lg;
    border-radius: $radius-md;
    border: 1px solid #fde68a;
    background: $admin-warning-bg;
    font-size: $font-size-sm;
    color: #78350f;

    ul {
        margin: 0;
        padding-left: 1.1rem;
    }

    li + li { margin-top: $spacing-xs; }
}

.consequences__title {
    margin: 0 0 $spacing-xs;
    font-weight: 600;
}
</style>
